"use client";

import { logClientError } from "./actions";
import {
  classifyChunkError,
  isChunkLoadError,
  isLocalDevHost,
} from "./noise-filters";

/**
 * Report a React render error caught by a route-level error.tsx
 * boundary into the same `error_log` pipeline as the window-level
 * ErrorCapture listeners (admin/query → "Recent errors").
 *
 * The `react-error-boundary` kind was already part of
 * `logClientError`'s contract — ErrorCapture's docstring lists
 * render errors as the gap this closes ("those need a route-level
 * error.tsx boundary which we don't have yet. v2 work").
 *
 * Mirrors ErrorCapture's posture:
 *   - localhost / 127.0.0.1 is skipped (dev noise stays out of
 *     production data)
 *   - fire-and-forget with a swallowed rejection (an error
 *     reporter that errors loudly would loop)
 *   - same fingerprint shape (message first-100 + stack first-200)
 *     so the admin viewer's dedup reads consistently across kinds
 *
 * No 60s dedup cache here: a boundary fires once per thrown render
 * error (not per event-loop tick), so the window-level cache's
 * multi-fire problem doesn't apply.
 */
export function reportBoundaryError(error: Error & { digest?: string }) {
  if (typeof window === "undefined") return;

  // Server-component errors arrive with a generic message + digest
  // (Next strips the real message in production to avoid leaking
  // server details) — keep the digest so the admin can match it to
  // the server-side log line.
  const message =
    error.message && error.message.trim().length > 0
      ? error.message
      : `(no message)${error.digest ? ` digest=${error.digest}` : ""}`;
  const stack = error.stack ?? null;

  // A ChunkLoadError that reaches a render boundary is a stale deploy — the
  // tab's lazy chunk hashes were replaced by a newer build. Reload once for
  // the fresh bundle and DON'T log it (shares classifyChunkError + the reload
  // guard with the window listeners, so a multi-chunk burst reloads once and
  // skips the rest). Only a chunk error that PERSISTS on the fresh page load
  // (a real network/CDN problem) falls through to report below.
  //
  // Ahead of the dev-host check on purpose - see isLocalDevHost. The reload is
  // recovery, and a dev machine rebuilding under a live server is the single
  // likeliest place to need it.
  if (isChunkLoadError(message, error)) {
    const decision = classifyChunkError();
    if (decision === "reload") setTimeout(() => window.location.reload(), 0);
    if (decision !== "report") return;
  }

  // Everything past here writes to error_log, which dev origins never do.
  if (isLocalDevHost()) return;

  const fingerprint = `${message.slice(0, 100)}::${stack ? stack.slice(0, 200) : ""}`;

  void logClientError({
    kind: "react-error-boundary",
    message,
    stack,
    fingerprint,
    url: window.location.pathname + window.location.search,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? null,
  }).catch(() => {});
}
